/**
 * Latency Bar Chart Component
 * Horizontal bar chart showing latency breakdown (total, cache, backend)
 */

import { Card } from '@/components/ui/Card';
import { useI18n } from '@/i18n';

export interface LatencyChartProps {
  avgLatency: number;      // Total average latency
  cacheLatency: number;    // Cache lookup latency
  backendLatency: number;  // Backend request latency
  className?: string;
}

interface BarData {
  labelKey: 'total' | 'cacheLatency' | 'backend';
  value: number;
  color: string;
}

function formatLatency(value: number): string {
  if (value < 1) return '<1ms';
  return `${Math.round(value)}ms`;
}

export function LatencyChart({
  avgLatency,
  cacheLatency,
  backendLatency,
  className = '',
}: LatencyChartProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.charts;

  const bars: BarData[] = [
    { labelKey: 'total', value: avgLatency, color: '#3B82F6' },      // blue-500
    { labelKey: 'cacheLatency', value: cacheLatency, color: '#10B981' },    // green-500
    { labelKey: 'backend', value: backendLatency, color: '#F59E0B' }, // yellow-500
  ];

  // Find max value for scaling
  const maxValue = Math.max(...bars.map(b => b.value), 1);

  return (
    <Card className={`${className}`}>
      <h3 className="text-sm font-medium text-gray-600 mb-4">{t.avgLatency}</h3>
      <div className="space-y-3" style={{ minHeight: 160 }}>
        {bars.map((bar) => {
          const widthPercent = (bar.value / maxValue) * 100;
          const label = t[bar.labelKey];
          return (
            <div key={bar.labelKey} className="flex items-center gap-3">
              <span className="text-sm text-gray-600 w-16">{label}</span>
              <div className="flex-1 h-8 bg-gray-100 rounded overflow-hidden relative">
                <div
                  className="h-full rounded transition-all duration-500 ease-out flex items-center"
                  style={{
                    width: `${Math.max(widthPercent, 2)}%`,
                    backgroundColor: bar.color,
                  }}
                >
                  <span className="text-xs font-medium text-white px-2 whitespace-nowrap">
                    {formatLatency(bar.value)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
